/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState } from "react";

import Head from "next/head";
import styled from "styled-components";

import TranscriptionDemo from "../../../components/Splash/Scribe/TranscriptionDemo";

export default function ViennaScribeHome() {
  const [isDropdownVisible, setDropdownVisibility] = useState(false);
  return (
    <>
      <Head>
        <title>Vienna Scribe</title>
      </Head>
      <Main>
        {/* Header */}
        <Navbar>
          <Title>Scribe.</Title>
          <Placeholder />
          <a href="https://vienna.earth/meta/" target="_blank" rel="noopener noreferrer">
            <HomeButton>Home</HomeButton>
          </a>
        </Navbar>
        {/* Main Section */}
        <Section>
          <GrayHeader>Transcription</GrayHeader>
          <BlackHeader>Transformed</BlackHeader>
          <CenterContainer style={{ marginTop: "5px" }}>
            <a
              href="https://apps.apple.com/us/app/vienna-scribe/id6444012049"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DownloadButton />
            </a>
          </CenterContainer>
          <CenterContainer style={{ marginBottom: "0" }}>
            <img
              style={{ width: "100%", maxWidth: "475px" }}
              src="https://vienna-prod.imgix.net/754d4b3a-cde6-42f0-83a6-d150cfce9b37.png"
              alt="Screenshot"
              draggable="false"
            />
          </CenterContainer>
        </Section>
        <BlackLine />
        {/* Accuracy Section: TODO */}
        {/* Use Cases Section */}
        <FirstSection>
          <GraySubtitle style={{ marginTop: "30px" }}>Precision,</GraySubtitle>
          <BlackSubtitle>to the point.</BlackSubtitle>
          <CenterContainer>
            <TranscriptionDemo />
          </CenterContainer>
        </FirstSection>
        <BlackLine />
        {/* Technical Section */}
        <SecondSection style={{ paddingBottom: "0px" }}>
          <GraySubtitle style={{ marginTop: "30px" }}>Perfect transcription,</GraySubtitle>
          <BlackSubtitle>within reach.</BlackSubtitle>
          <CenterContainer>
            <GreenImage style={{ marginTop: "25px", marginBottom: "25px" }} />
          </CenterContainer>
          <ColumnsContainer style={{ marginBottom: "40px" }}>
            <Column>
              <GaramondBlackListTextRightAlign>For every</GaramondBlackListTextRightAlign>
            </Column>
            <Column>
              <GaramondGrayListTextLeftAlign>Diagnosis</GaramondGrayListTextLeftAlign>
              <GaramondGrayListTextLeftAlign>Meeting</GaramondGrayListTextLeftAlign>
              <GaramondGrayListTextLeftAlign>Lecture</GaramondGrayListTextLeftAlign>
              <GaramondGrayListTextLeftAlign>Interview</GaramondGrayListTextLeftAlign>
              <GaramondGrayListTextLeftAlign>Meditation</GaramondGrayListTextLeftAlign>
              <GaramondGrayListTextLeftAlign>Memory</GaramondGrayListTextLeftAlign>
              <GaramondGrayListTextLeftAlign>Idea</GaramondGrayListTextLeftAlign>
            </Column>
          </ColumnsContainer>
        </SecondSection>
        {/* Privacy Section */}
        <ThirdSection>
          <GraySubtitle style={{ marginTop: "75px" }}>Locked-down</GraySubtitle>
          <WhiteSubtitle>Privacy</WhiteSubtitle>
          <WhiteImage style={{ marginTop: "25px", marginBottom: "25px" }} />
          <GraySubtitle>Files stored</GraySubtitle>
          <GraySubtitle style={{ marginBottom: "25px" }}>on device.</GraySubtitle>
          <GraySubtitle>Completely</GraySubtitle>
          <GraySubtitle style={{ marginBottom: "90px" }}>Anonymous.</GraySubtitle>
        </ThirdSection>
        {/* Languages Section */}
        <FourthSection>
          <SmallBlackSubtitle style={{ marginTop: "40px" }}>70+ Languages</SmallBlackSubtitle>
          <SmallGraySubtitle style={{ marginBottom: "10px" }}>automatically detected</SmallGraySubtitle>
          <DropdownTrigger onClick={() => setDropdownVisibility(!isDropdownVisible)}>
            {isDropdownVisible ? "hide all" : "see all"}
          </DropdownTrigger>
          {isDropdownVisible && (
            <CenterContainer>
              <Dropdown>
                {/* Add the languages you want to display here */}
                <Text>Afrikaans, Arabic, Armenian, Azerbaijani</Text>

                <Text>Belarusian, Bosnian, Bulgarian, Catalan</Text>

                <Text>Chinese, Croatian, Czech, Danish</Text>

                <Text>Dutch, English, Estonian, Finnish</Text>

                <Text>French, Galician, German, Greek</Text>

                <Text>Hebrew, Hindi, Hungarian, Icelandic</Text>

                <Text>Indonesian, Italian, Japanese, Kannada</Text>

                <Text>Kazakh, Korean, Latvian, Lithuanian</Text>

                <Text>Macedonian, Malay, Marathi, Maori</Text>

                <Text>Nepali, Norwegian, Persian, Polish</Text>

                <Text>Portuguese, Romanian, Russian, Serbian</Text>

                <Text>Slovak, Slovenian, Spanish, Swahili</Text>

                <Text>Swedish, Tagalog, Tamil, Thai, Turkish</Text>

                <Text>Ukrainian, Urdu, Vietnamese, Welsh</Text>
              </Dropdown>
            </CenterContainer>
          )}
          <SmallBlackSubtitle style={{ marginTop: "50px" }}>Supported Files,</SmallBlackSubtitle>
          <SmallGraySubtitle style={{ marginBottom: "10px" }}>easily importable</SmallGraySubtitle>
          <Text>mp3, mp4, m4a,wav,webm</Text>
          <SmallBlackSubtitle style={{ marginTop: "50px" }}>Exportable Text</SmallBlackSubtitle>
          <SmallGraySubtitle style={{ marginBottom: "10px" }}>with format promting</SmallGraySubtitle>
          <Text style={{ marginBottom: "60px" }}>for emails, summaries, blogposts and more.</Text>
        </FourthSection>
        <BlackLine />
        {/* Pricing Section */}
        <FifthSection>
          <GraySubtitle style={{ marginTop: "25px" }}>Simple &</GraySubtitle>
          <BlackSubtitle style={{ marginBottom: "50px" }}>Unlimited</BlackSubtitle>
          <Text style={{ marginBottom: "55px" }}>$9.99 per month.</Text>
          <CenterContainer style={{ marginBottom: "50px" }}>
            <a
              href="https://apps.apple.com/us/app/vienna-scribe/id6444012049"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DownloadButton />
            </a>
          </CenterContainer>

          <a href="https://vienna.earth/meta/scribe/faq" target="_blank" rel="noopener noreferrer">
            <Text style={{ marginTop: "50px" }}>FAQ</Text>
          </a>
          <a href="https://vienna.earth/meta/scribe/privacy" target="_blank" rel="noopener noreferrer">
            <Text>Privacy Policy</Text>
          </a>
          <a href="https://vienna.earth/meta/scribe/accuracy" target="_blank" rel="noopener noreferrer">
            <Text>Testing Details</Text>
          </a>
        </FifthSection>
      </Main>
    </>
  );
}

// Layout
const Main = styled.main`
  background-color: #fafafa;
  min-height: 100vh;
`;

const Navbar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
`;

const HomeButton = styled.button`
  font-family: "Syne", sans-serif;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #333;
  &:hover {
    text-decoration: underline;
  }
`;

const Section = styled.section`
  padding: 20px;
  padding-bottom: 0;
  text-align: center;
`;

const FirstSection = styled.section`
  padding: 20px;
  text-align: center;
`;

const SecondSection = styled.section`
  padding: 20px;
  text-align: center;
`;

const ThirdSection = styled.section`
  padding: 20px;
  background-color: black; // Set background color to black
  color: white; // Set font color to white
  text-align: center;
`;

const FourthSection = styled.section`
  padding: 20px;
  text-align: center;
  background-color: #fafafa; // Same as the first and second sections
`;

const FifthSection = styled.section`
  padding: 20px;
  text-align: center;
  background-color: #fafafa; // Adjust as needed
`;

const DownloadButton = styled.div`
  background-image: url("https://vienna-prod.imgix.net/7518abb4-25e3-4f4e-ae0b-8f0448b4b2b9.svg?auto=format");
  background-size: contain; // to make sure the image fits into the div
  background-repeat: no-repeat; // to prevent image tiling
  background-position: center; // to center the image
  width: 130px;
  height: 44.32px;
  cursor: pointer; // to indicate the div is clickable
`;

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
`;

const ColumnsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start; // align to the top of the container
`;

const Column = styled.div`
  margin: 0 0;
`;

// Typography
const Title = styled.h1`
  font-size: 24px;
  font-weight: unset;
  left: 50%;
  font-family: "ViennaGaramondLight-Regular";
  user-select: none;

  @media only screen and (min-width: 768px) {
    font-size: 32px;
  }

  @media only screen and (min-width: 1024px) {
    font-size: 40px;
  }
`;

const Placeholder = styled.div`
  width: 80px;
`;

const BlackHeader = styled.div`
  font-size: 55px; // Set to your mobile size
  margin-bottom: 30px;
  font-family: "ViennaGaramondLight-Italic";

  @media only screen and (min-width: 768px) {
    font-size: 65px; // Set to your tablet size or adjust as needed
  }

  @media only screen and (min-width: 1024px) {
    font-size: 100px; // Set to your desktop size or adjust as needed
  }
`;

const GrayHeader = styled.div`
  font-size: 55px; //set for mobiel size
  margin-bottom: 20px;
  font-family: "ViennaGaramondLight-Regular";
  color: gray;
  margin-bottom: -5px;

  @media only screen and (min-width: 768px) {
    font-size: 65px; // Set to your tablet size or adjust as needed
  }

  @media only screen and (min-width: 1024px) {
    font-size: 100px; // Set to your desktop size or adjust as needed
  }
`;

const Text = styled.p`
  font-size: 16px;
  margin-bottom: 10px;
  font-family: "Syne", sans-serif;
  @media only screen and (min-width: 768px) {
    font-size: 19px; // Tablet Size
  }

  @media only screen and (min-width: 1024px) {
    font-size: 19px; // Desktop Size
  }
`;

const BlackLine = styled.div`
  height: 1px;
  background-color: black;
  width: 100%;
`;

const BlackSubtitle = styled.div`
  font-size: 40px; // Adjust as needed
  margin-bottom: 20px;
  font-family: "ViennaGaramondLight-Regular";
  @media only screen and (min-width: 768px) {
    font-size: 45px; // Tablet Size
  }

  @media only screen and (min-width: 1024px) {
    font-size: 50px; // Desktop Size
  }
`;

const GreenImage = styled.div`
  background-image: url("https://vienna-prod.imgix.net/d56a5238-367b-4471-bdd8-aa378ee682e6.png?auto=format");
  background-size: cover; // this will make sure your image covers the div completely
  background-position: center; // this will center your image in the div
  background-repeat: no-repeat; // this will prevent your image from repeating in the div
  width: 162px;
  height: 162px;
  margin-bottom: 20px;

  @media only screen and (min-width: 768px) {
    width: 182px; // Tablet Size
    height: 182px;
  }

  @media only screen and (min-width: 1024px) {
    width: 220px; // Desktop Size
    height: 220px;
  }
`;

const GaramondBlackListText = styled.div`
  font-size: 40px;
  margin-bottom: 10px;
  font-family: "ViennaGaramondLight-Regular";

  @media only screen and (min-width: 768px) {
    font-size: 45px; // Tablet Size
  }

  @media only screen and (min-width: 1024px) {
    font-size: 50px; // Desktop Size
  }
`;
const GaramondGrayListText = styled.div`
  font-size: 40px;
  margin-bottom: 0px;
  color: gray;
  font-family: "ViennaGaramondLight-Regular";

  @media only screen and (min-width: 768px) {
    font-size: 45px; // Tablet Size
  }

  @media only screen and (min-width: 1024px) {
    font-size: 50px; // Desktop Size
  }
`;
const GaramondBlackListTextRightAlign = styled(GaramondBlackListText)`
  text-align: right;
`;
const GaramondGrayListTextLeftAlign = styled(GaramondGrayListText)`
  text-align: left;
  padding-left: 10px;
`;

const WhiteSubtitle = styled.div`
  font-size: 40px; // Adjust as needed
  margin-bottom: 20px;
  color: white;
  font-family: "ViennaGaramondLight-Regular";

  @media only screen and (min-width: 768px) {
    font-size: 45px; // Tablet Size
  }

  @media only screen and (min-width: 1024px) {
    font-size: 50px; // Desktop Size
  }
`;

const GraySubtitle = styled.div`
  font-size: 40px; // Adjust as needed
  margin-bottom: -3px;
  color: gray;
  font-family: "ViennaGaramondLight-Regular";
  @media only screen and (min-width: 768px) {
    font-size: 45px; // Tablet Size
  }

  @media only screen and (min-width: 1024px) {
    font-size: 50px; // Desktop Size
  }
`;

const SmallGraySubtitle = styled.div`
  font-size: 35px; // Adjust as needed
  margin-bottom: -3px;
  color: black;
  font-family: "ViennaGaramondLight-Regular";
  @media only screen and (min-width: 768px) {
    font-size: 40px; // Tablet Size
  }

  @media only screen and (min-width: 1024px) {
    font-size: 45px; // Desktop Size
  }
`;

const SmallBlackSubtitle = styled.div`
  font-size: 35px; // Adjust as needed
  margin-bottom: -3px;
  color: gray;
  font-family: "ViennaGaramondLight-Regular";
  @media only screen and (min-width: 768px) {
    font-size: 40px; // Tablet Size
  }

  @media only screen and (min-width: 1024px) {
    font-size: 45px; // Desktop Size
  }
`;

//images

const WhiteImage = styled.div`
  background-image: url("https://vienna-prod.imgix.net/51f2ba9d-6d56-4d6a-babb-83de0434cb17.svg?auto=format");
  background-size: contain; // to make sure the image fits into the div
  background-repeat: no-repeat; // to prevent image tiling
  background-position: center; // to center the image
  width: 100px; // Adjust as needed
  height: 100px; // Adjust as needed
  margin: 20px auto; // Center the image and give it some margin from top and bottom
`;

const Dropdown = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 10px 0;
  background-color: #fafafa;
  border: 0px solid #ccc;
  max-width: 400px;

  li {
    padding: 10px;
    border-bottom: 1px solid #fafafa;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #fafafa;
    }
  }
`;

const DropdownTrigger = styled(Text)`
  cursor: pointer;
  color: black; // adjust the color to fit your design
  text-decoration: underline;
`;
